import Form from 'components/Form/Form';
import Button from 'components/Button/Button';
import React, { useEffect, useState } from 'react';
import { Paragraph, Title, Text } from 'components/Typography';
import AuthWrapper from 'components/Auth';
import { FormField, FormItem } from 'components/Form/FormItem';
import { useLogin } from 'api/hooks/useLogin';
import { useSetupMfa } from 'api/hooks/useSetupMfa';
import { useVerifyCode } from 'api/hooks/useVerifyCode';
import { TFormFields } from 'components/Form/types';
import { TFormFieldTypes } from 'components/Form/FormItem/types';
import { useForm } from 'antd/es/form/Form';
import Alert from 'components/Alert/Alert';
import { TLoginDataUi } from 'data/types';
import { useNavigate } from 'react-router-dom';
import { useGetUserQuery } from 'redux/apiSlices';
import { setLogedInfirebase, setUser } from 'redux/slices';
import { useAppDispatch } from 'redux/store';
import QRCodE from 'qrcode';
import { TOnFinishDataUi, TSetupMfaDataUi } from 'data/types/auth.types';
import {
  useAuthToFirebaseQuery,
  useGetFirebaseTokenQuery,
} from 'redux/apiSlices/firebase.slice';
import useGetPaymentInfo from 'api/hooks/useGetPaymentInfo';
import { sendGTMDataLayer } from 'helpers/utils';
import LinkWithReferrerId from '../components/LinkWithReferrerId/LinkWithReferrerId';

const loginFields: TFormFields = {
  email: {
    fieldType: TFormFieldTypes.INPUT,
    rules: [
      { required: true, message: 'Please enter your email address!' },
      { type: 'email', message: 'Incorrect Email Address!' },
    ],
    placeholder: 'Email address',
  },
  password: {
    fieldType: TFormFieldTypes.POSSWORD_CONFIRM,
    rules: [{ required: true, message: 'Please enter your password!' }],
    placeholder: 'Password',
  },
};

const setupMFAFields: TFormFields = {
  mfaCode: {
    fieldType: TFormFieldTypes.INPUT,
    rules: [
      { required: true, message: 'Please enter your 6 digit MFA code' },
      { type: 'string', message: 'Incorrect code!', len: 6 },
    ],
    placeholder: 'Enter your 6 digit MFA code',
  }
};

export const Login = () => {
  const [setupMFA, setSetupMFA] = useState<boolean>(false);
  const [MFACodeRequired, setMFACodeRequired] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [MFAQRString1, setMFAQRString1] = useState<string>();
  const [form] = useForm();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, refetch: login, error, data: authData } = useLogin();
  const { isLoading: isLoading1, refetch: verifyMfaSetup, error: error1, data: authData1 } = useSetupMfa();
  const { isLoading: isLoading2, refetch: verifyMfaCode, error: error2, data: authData2 } = useVerifyCode();

  const fetchUserSkip = () => {
    if (authData1 || authData2) {
      return false;
    }

    return true;
  };

  const { isLoading: getUserLoading, data: user } = useGetUserQuery(authData2, {
    skip: fetchUserSkip(),
    refetchOnMountOrArgChange: true,
  });

  const {
    data: token,
    isLoading: getFirebaseTokenLoading,
    isError: isFetFirebaseTokenError,
  } = useGetFirebaseTokenQuery(null, {
    skip: !user,
    refetchOnMountOrArgChange: true,
  });
  const {
    isSuccess: authToFirebaseSuccess,
    isLoading: authToFirebaseLoading,
    isError: isAuthToFirebaseError,
  } = useAuthToFirebaseQuery(token || '', {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });
  const {
    refetch: getPayment,
    isLoading: paymentLoading,
    isSuccess: paymentSuccess,
    error: paymentError,
  } = useGetPaymentInfo();

  useEffect(() => {
    if (user?.organization?.id && authToFirebaseSuccess) {
      dispatch(setLogedInfirebase(authToFirebaseSuccess));
      getPayment(user?.organization?.id);
    }
  }, [user, authToFirebaseSuccess]);

  useEffect(() => {
    const func = async () => {
      if (authData) {
        if (authData.isMFASetupRequired) {
          setSetupMFA(true);
          const qrCodeImageSource = await QRCodE.toDataURL(`otpauth://totp/AWSCognito:${authData.user.getUsername()}?secret=${authData.setupMFACode}&issuer=Zest(${emailAddress})`);
          setMFAQRString1(qrCodeImageSource);
        }
        else if (authData.isMFACodeRequired) {
          setMFACodeRequired(true);
        }
      }
    };

    func();

  }, [authData])

  const onFinish = (values: TOnFinishDataUi) => {
    if (setupMFA) {
      verifyMfaSetup({mfaCode: (values as TSetupMfaDataUi).mfaCode, user: authData?.user});
      sendGTMDataLayer({
        event: 'mfa_setup',
        emailAddress: authData?.user.getUsername()
      });
    }
    else if (MFACodeRequired) {
      verifyMfaCode({mfaCode: (values as TSetupMfaDataUi).mfaCode, user: authData?.user});
    }
    else {
      setEmailAddress((values as TLoginDataUi).email);
      login({ email: (values as TLoginDataUi).email, password: (values as TLoginDataUi).password });
    }
  };
  const isFirebaseRelatedError =
    isFetFirebaseTokenError || isAuthToFirebaseError || !!paymentError;

  useEffect(() => {
    if (
      (user && authToFirebaseSuccess && paymentSuccess) ||
      isFirebaseRelatedError
    ) {
      dispatch(setUser(user));
      navigate('/login');
    }
  }, [user, authToFirebaseSuccess, paymentSuccess, isFirebaseRelatedError]);

  const signupClicked = () => {
    sendGTMDataLayer({
        event: 'signup_clicked'
    });
  }
  return (
    <AuthWrapper withPadding logo>
      <div className="auth-title-block auth-align">
        {!setupMFA && <Title fontlevel={3} className="mb-8 auth-align">
          Log Into My Account
        </Title>}
        {setupMFA && <><Title fontlevel={3} className="mb-3 auth-align">
          Please Setup Multi-Factor Authentication (MFA)
        </Title> <Text className="mb-3 auth-align" fontlevel={4}>Scan the QR code or type in manually, then type the next MFA code</Text></>}
      </div>
      <Alert className="auth-align" type="error" message={error?.message || error1?.message || error2?.message} />
      <Form
        form={form}
        name="basic"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
      >
        {setupMFA && <><Paragraph fontlevel={6}><img
                data-amplify-qrcode
                src={MFAQRString1}
                alt="qr code"
                width="228"
                height="228"
              /> <Text fontlevel={4}>{authData?.setupMFACode}</Text></Paragraph></>}

        {!(setupMFA || MFACodeRequired) && Object.keys(loginFields).map(field => {
          const props = loginFields[field];
          return <FormField key={field} name={field} {...props} />;
        })}
        {(setupMFA || MFACodeRequired) && Object.keys(setupMFAFields).map(field => {
          const props = setupMFAFields[field];
          return <FormField key={field} name={field} {...props} />;
        })}

        {!(setupMFA || MFACodeRequired) && <Paragraph fontlevel={6} className="auth-link-wrapper mb-5">
          <LinkWithReferrerId to="/reset-password">
            Forgot password?
          </LinkWithReferrerId>
        </Paragraph>}
        <FormItem>
          <Button
            type="primary"
            htmlType="submit"
            loading={
              isLoading || isLoading1 || isLoading2 ||
              getUserLoading ||
              getFirebaseTokenLoading ||
              authToFirebaseLoading ||
              paymentLoading
            }
          >
            {!setupMFA && 'Sign In'}
            {setupMFA && 'Submit Code'}
          </Button>
        </FormItem>
      </Form>
      {!setupMFA && <Paragraph fontlevel={6} className="auth-link-wrapper auth-align mt-4">
        Don’t have an account?{' '}
        <LinkWithReferrerId to="/sign-up" onClick={signupClicked}>Sign Up</LinkWithReferrerId>
      </Paragraph>}
    </AuthWrapper>
  );
};

export default Login;


